/**
 * @file EdgeFade atom — overflow fade overlay for horizontal and vertical scrollers.
 * @description Narrowed atom wrapping expo-linear-gradient: an absolutely
 * positioned surface-base gradient hugging one edge of a relative parent,
 * signalling that content continues past the clipped edge (SE-5593). The
 * gradient endpoints match the `surface-base` token per theme (background-0:
 * 255 255 255 light / 18 18 18 dark — docs/design-system/tokens.md); theme
 * selection comes in via the `isDark` prop because atoms are stateless and
 * cannot read providers.
 * @module components/atoms/EdgeFade
 */
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import type { AccessibilityProps } from "react-native";

/**
 * Which edge of the relative parent the fade hugs. `bottom` is the vertical
 * scroll fade (SE-8486, W69): 48 px over the last row of a scrolling list.
 */
export type EdgeFadeSide = "left" | "right" | "bottom";

/** Props for the EdgeFade atom. */
export interface EdgeFadeProps extends AccessibilityProps {
  /** Edge of the relative parent to hug (default "right") */
  readonly side?: EdgeFadeSide;
  /** Dark-mode flag — picks the surface-base endpoint for the theme */
  readonly isDark?: boolean;
  /** Test identifier forwarded to the gradient host */
  readonly testID?: string;
}

/** surface-base endpoints (background-0 light) — transparent → opaque. */
const LIGHT_COLORS = ["rgba(255,255,255,0)", "rgb(255,255,255)"] as const;
/** surface-base endpoints (background-0 dark) — transparent → opaque. */
const DARK_COLORS = ["rgba(18,18,18,0)", "rgb(18,18,18)"] as const;

/** Shared overlay geometry — spans the parent's height at 32px (w-8). */
const BASE_STYLE = {
  position: "absolute",
  top: 0,
  bottom: 0,
  width: 32,
} as const;
/** Right-edge placement. */
const RIGHT_STYLE = { ...BASE_STYLE, right: 0 } as const;
/** Left-edge placement. */
const LEFT_STYLE = { ...BASE_STYLE, left: 0 } as const;
/** Bottom-edge placement — spans the parent's width at 48px (h-12). */
const BOTTOM_STYLE = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 48,
} as const;

/** Placement and gradient direction per side. */
const SIDE_GEOMETRY: Record<
  EdgeFadeSide,
  {
    readonly style:
      | typeof RIGHT_STYLE
      | typeof LEFT_STYLE
      | typeof BOTTOM_STYLE;
    readonly end: { readonly x: number; readonly y: number };
  }
> = {
  right: { style: RIGHT_STYLE, end: { x: 1, y: 0 } },
  left: { style: LEFT_STYLE, end: { x: 1, y: 0 } },
  bottom: { style: BOTTOM_STYLE, end: { x: 0, y: 1 } },
};

/** The gradient always starts at (0, 0). */
const START = { x: 0, y: 0 } as const;

/**
 * Renders a theme-matched fade overlay on one edge of a relative parent.
 * The overlay never intercepts pointer events; the parent must be
 * `relative` and own the clipping (`overflow-hidden`).
 * @param props - Component props
 * @param props.side - Edge of the parent to hug (default "right")
 * @param props.isDark - Dark-mode flag for the surface endpoint
 * @param props.testID - Test identifier forwarded to the gradient host
 * @param props.accessibilityProps - Accessibility props forwarded to the
 *   gradient host
 * @returns The positioned gradient overlay
 */
function EdgeFade({
  side = "right",
  isDark = false,
  testID,
  ...accessibilityProps
}: EdgeFadeProps) {
  const surface = isDark ? DARK_COLORS : LIGHT_COLORS;
  // Left runs opaque→transparent (the fade sits at the gradient's start);
  // right and bottom run transparent→opaque towards the clipped edge.
  const colors: readonly [string, string] =
    side === "left" ? [surface[1], surface[0]] : surface;
  const { style, end } = SIDE_GEOMETRY[side];
  return (
    <LinearGradient
      {...accessibilityProps}
      testID={testID}
      pointerEvents="none"
      colors={colors}
      start={START}
      end={end}
      style={style}
    />
  );
}

export { EdgeFade };
